import { Link } from 'react-router-dom';
import { Bot, Workflow, Brain, TrendingDown, ArrowRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { isEnglishPath } from '../utils/i18nRoutes';

interface ServiceTeaser {
  icon: React.ElementType;
  title: string;
  description: string;
  linkPath: string;
  linkText: string;
}

const serviceTeasersDe: ServiceTeaser[] = [
  {
    icon: Bot,
    title: 'KI-Telefonassistent',
    description:
      'Automatisierte Anrufannahme und Anfragenbearbeitung rund um die Uhr. Entlasten Sie Ihr Team von Routineanfragen und verkürzen Sie die Wartezeit für Ihre Anrufer.',
    linkPath: '/leistungen/ki-telefonassistent',
    linkText: 'KI-Telefonassistent für automatisierte Anrufannahme',
  },
  {
    icon: Workflow,
    title: 'Prozessautomatisierung',
    description:
      'Wiederkehrende Geschäftsprozesse – von Rechnungsverarbeitung bis Dateneingabe – werden automatisiert und in Ihre bestehenden Systeme integriert.',
    linkPath: '/leistungen/prozessautomatisierung',
    linkText: 'Prozessautomatisierung für wiederkehrende Geschäftsabläufe',
  },
  {
    icon: Brain,
    title: 'KI-Integration',
    description:
      'Einbau von KI-Komponenten in Ihre vorhandene IT-Infrastruktur: Chatbots, Dokumentenverarbeitung und Datenanalyse, angepasst an Ihre Prozesse.',
    linkPath: '/leistungen/ki-integration',
    linkText: 'KI-Integration in bestehende IT-Systeme',
  },
  {
    icon: TrendingDown,
    title: 'Kostenoptimierung',
    description:
      'Senkung von Betriebskosten in Energie, Telekommunikation, Software und Beschaffung durch Vertragsanalyse und Neuverhandlung – erfolgsbasiert vergütet.',
    linkPath: '/leistungen/kostenoptimierung',
    linkText: 'Kostenoptimierung für Energie, Telekom und Software',
  },
];

const serviceTeasersEn: ServiceTeaser[] = [
  {
    icon: Bot,
    title: 'AI Phone Assistant',
    description:
      'Automated call answering and inquiry processing around the clock. Relieve your team of routine requests and shorten wait times for your callers.',
    linkPath: '/en/services/ai-phone-assistant',
    linkText: 'AI phone assistant for automated call answering',
  },
  {
    icon: Workflow,
    title: 'Process Automation',
    description:
      'Recurring business processes — from invoice processing to data entry — are automated and integrated into your existing systems.',
    linkPath: '/en/services/process-automation',
    linkText: 'Process automation for recurring business workflows',
  },
  {
    icon: Brain,
    title: 'AI Integration',
    description:
      'Integration of AI components into your existing IT infrastructure: chatbots, document processing and data analysis, tailored to your processes.',
    linkPath: '/en/services/ai-integration',
    linkText: 'AI integration into existing IT systems',
  },
  {
    icon: TrendingDown,
    title: 'Cost Optimization',
    description:
      'Reduction of operating costs in energy, telecommunications, software and procurement through contract analysis and renegotiation — success-based.',
    linkPath: '/en/services/cost-optimization',
    linkText: 'Cost optimization for energy, telecom and software',
  },
];

export default function Services() {
  const location = useLocation();
  const isEn = isEnglishPath(location.pathname);
  const serviceTeasers = isEn ? serviceTeasersEn : serviceTeasersDe;

  const h2 = isEn ? 'Our Services' : 'Unsere Leistungen';
  const intro = isEn
    ? 'Four service areas that complement each other: AI automation reduces manual effort, cost optimization reduces spending. Together they measurably reduce your operating costs.'
    : 'Vier Leistungsbereiche, die sich ergänzen: KI-Automatisierung senkt manuellen Aufwand, Kostenoptimierung senkt Ausgaben. Beide zusammen senken Ihre Betriebskosten messbar.';

  return (
    <section id="services" className="py-20 px-6 bg-soft dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-dark dark:text-white mb-4">
            {h2}
          </h2>
          <p className="text-lg text-muted dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {intro}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {serviceTeasers.map((teaser, index) => (
            <div
              key={teaser.linkPath}
              className="card-base p-8 relative overflow-hidden"
            >
              <span className="absolute top-4 right-6 text-6xl font-bold text-primary-tint2 dark:text-gray-700 select-none leading-none">
                {String(index + 1).padStart(2, '0')}
              </span>
              <div className="relative z-10">
                <div className="w-12 h-12 bg-primary-tint rounded-xl flex items-center justify-center mb-5">
                  <teaser.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-dark dark:text-white mb-3">
                  {teaser.title}
                </h3>
                <p className="text-muted dark:text-gray-300 leading-relaxed mb-5 text-sm">
                  {teaser.description}
                </p>
                <Link
                  to={teaser.linkPath}
                  className="inline-flex items-center gap-2 text-primary dark:text-accent-light font-semibold text-sm hover:gap-3 transition-all"
                >
                  {teaser.linkText}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
